/**
 * Authentication Service for Personal Trainer App
 *
 * Provides a comprehensive authentication service layer that handles all
 * authentication-related API calls including login, registration, logout,
 * token refresh, password reset, and user management.
 *
 * Features:
 * - Email/password authentication
 * - User registration with validation
 * - Automatic token refresh
 * - Password reset flows
 * - User profile management
 * - Device session management
 * - Comprehensive error handling
 * - Integration with token storage service
 */

import axios, { type AxiosInstance } from 'axios';

import { getAuthBaseUrl } from '../config/api';
import { HTTP_STATUS } from '../config/constants';
import { TWO_FACTOR_SIGN_IN_LAPSED } from '../config/two-factor.config';
import { getAuthConfig, getTokenStorage } from '../di/container';
import { invalidDataErrorFromZod } from '../errors/invalid-data.error';
import { apiClient, createBasicApiClient, createLazyProxy } from './api/client';
import {
  BearerLifecycleService,
  getBearerLifecycle,
} from './auth/bearer.lifecycle.service';
import { isApiError } from '../types/api/errors';
import { API_ENDPOINTS } from '../types/api.types';
import {
  AUTH_ERROR_CODES,
  SANCTUM_CONFIG,
  type AuthConfig,
} from '../types/auth';
import { TokenRefreshError } from '../types/auth/token.types';
import { dayjs } from '../utils/date';
import { appEnv } from '../utils/env';
import '../utils/string-extensions';
import {
  safeValidateLogin,
  safeValidateRegister,
  safeValidateUpdateProfile,
  safeValidateChangePassword,
  safeValidateDeleteAccount,
  safeValidateEmailVerificationLink,
  safeValidatePasswordResetConfirm,
  safeValidatePasswordResetRequest,
} from '../validation/auth.schemas';
import { safeValidateWaitlist } from '../validation/common.schemas';

import type { InvalidDataError } from '../errors/invalid-data.error';
import type {
  User,
  LoginRequest,
  LoginResponse,
  RegisterResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetConfirmRequest,
  PasswordResetConfirmResponse,
  ChangePasswordRequest,
  DeleteAccountRequest,
  LogoutRequest,
  LogoutResponse,
  EmailVerificationLink,
  AuthError,
  AuthToken,
  AuthErrorCode,
  SessionLoginResponse,
  SessionRegisterResponse,
  SessionPasswordResetConfirmResponse,
  UpdateProfileRequest,
  TokenValidationResult,
  TokenLoginApiResponse,
  TokenPasswordLoginApiResponse,
  TwoFactorProof,
  TwoFactorRequired,
  GoogleSignInRequest,
  AppleSignInRequest,
  PendingSignUp,
  PendingSignUpApiResponse,
  SocialSignInResult,
  SuccessApiResponse,
} from '../types';
import type { TokenRefreshService } from './auth/token.refresh.service';
import type {
  AuthServiceOptions,
  AuthServiceState,
} from '../types/auth/auth.types';
import type { ITokenStorageService } from '../types/auth/storage.types';
import type { RegisterInput } from '../validation/auth.schemas';
import type { WaitlistInput } from '../validation/common.schemas';
import type { ZodError } from 'zod';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Resolve the injected platform auth config to a fully-defaulted shape. This is
 * the SINGLE place the optional transport fields get their fallbacks, so the
 * readers below don't each re-apply `?? ...`. Throws if no config is registered
 * (a composition-root wiring bug). `baseURL` falls back to the env auth base;
 * the credential/XSRF flags default to stateless (false).
 */
const resolveAuthConfig = (): Required<AuthConfig> => {
  const cfg = getAuthConfig();
  return {
    mode: cfg.mode,
    baseURL: cfg.baseURL ?? getAuthBaseUrl(),
    withCredentials: cfg.withCredentials ?? false,
    withXSRFToken: cfg.withXSRFToken ?? false,
  };
};

/**
 * Build the default AuthService options from the injected platform auth config
 * (see `registerAuthConfig`). Mode + transport is a per-platform constant, not
 * runtime state.
 */
const buildDefaultOptions = (): Omit<
  Required<AuthServiceOptions>,
  'tokenStorageService'
> => {
  const cfg = resolveAuthConfig();
  return {
    baseURL: cfg.baseURL,
    mode: cfg.mode,
    maxRetryAttempts: 3,
    enableDeviceTracking: true,
    withCredentials: cfg.withCredentials,
  };
};

/**
 * The auth error code for a failed refresh. Only a refused bearer is an expired
 * session; a missing or unknown token is an invalid one; anything else never
 * reached a verdict on the bearer at all.
 */
const refreshFailureCode = (error: unknown): AuthErrorCode => {
  const reason = error instanceof TokenRefreshError ? error.reason : null;

  if (reason === 'rejected') return AUTH_ERROR_CODES.REFRESH_TOKEN_EXPIRED;
  if (reason === 'no-credentials' || reason === 'unrecognised') {
    return AUTH_ERROR_CODES.TOKEN_INVALID;
  }
  return AUTH_ERROR_CODES.NETWORK_ERROR;
};

/** The longest a session end waits for the server to revoke the bearer. */
const TOKEN_REVOKE_TIMEOUT_MS = 5000;

/**
 * An app's two-factor sign-in whose challenge has lapsed, or was never given,
 * refused without asking the API, in the shape it answers a spent challenge
 * with: a 422 under `challenge`.
 */
const lapsedTwoFactorSignIn = (): AuthError => ({
  code: AUTH_ERROR_CODES.INVALID_CREDENTIALS,
  message: TWO_FACTOR_SIGN_IN_LAPSED,
  field: 'challenge',
  details: { challenge: [TWO_FACTOR_SIGN_IN_LAPSED] },
});

// Dedicated auth client for CSRF and other auth-specific requests. Built lazily
// (and memoized) from the injected auth config so the transport — credentials,
// XSRF, base URL — matches the platform: stateless for mobile/token, stateful
// (cookie + XSRF) for web/session.
let authClientInstance: AxiosInstance | null = null;
const getAuthClient = (): AxiosInstance => {
  if (!authClientInstance) {
    const cfg = resolveAuthConfig();
    authClientInstance = createBasicApiClient({
      baseURL: cfg.baseURL,
      withCredentials: cfg.withCredentials,
      withXSRFToken: cfg.withXSRFToken,
      enableLogging: appEnv('development'),
    });
  }
  return authClientInstance;
};

/**
 * Convert ZodError to InvalidDataError for consistent error handling
 */
const createValidationError = (
  zodError: ZodError,
  context: string
): InvalidDataError =>
  invalidDataErrorFromZod(`${context} validation failed`, zodError);

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Create AuthError from various error sources
 */
const createAuthError = (
  error: unknown,
  defaultCode: AuthErrorCode = AUTH_ERROR_CODES.UNKNOWN_ERROR,
  defaultMessage: string = 'An unexpected error occurred'
): AuthError => {
  // Type narrow unknown error
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;

    // Handle Axios/API errors
    if (
      'response' in errorObj &&
      errorObj.response &&
      typeof errorObj.response === 'object'
    ) {
      const response = errorObj.response as Record<string, unknown>;

      if (
        'data' in response &&
        response.data &&
        typeof response.data === 'object'
      ) {
        const data = response.data as Record<string, unknown>;

        // Laravel validation errors
        if (
          'errors' in data &&
          data.errors &&
          typeof data.errors === 'object'
        ) {
          const errors = data.errors as Record<string, string[]>;
          const firstField = Object.keys(errors)[0] as string | undefined;
          const firstError = firstField ? errors[firstField]?.[0] : undefined;

          return {
            code: AUTH_ERROR_CODES.INVALID_CREDENTIALS,
            message:
              (typeof data.message === 'string' ? data.message : firstError) ||
              defaultMessage,
            field: firstField,
            details: errors,
          };
        }

        // Standard API error response, with its meta when it has one
        return {
          code:
            typeof data.code === 'string'
              ? (data.code as AuthErrorCode)
              : defaultCode,
          message:
            typeof data.message === 'string' ? data.message : defaultMessage,
          ...(data.meta && typeof data.meta === 'object'
            ? { meta: data.meta as Record<string, unknown> }
            : {}),
        };
      }
    }

    // Network or other errors
    if (
      ('code' in errorObj && errorObj.code === 'NETWORK_ERROR') ||
      ('message' in errorObj &&
        typeof errorObj.message === 'string' &&
        errorObj.message.includes('Network Error'))
    ) {
      return {
        code: AUTH_ERROR_CODES.NETWORK_ERROR,
        message:
          'Network connection failed. Please check your internet connection.',
      };
    }

    // Default error with message
    return {
      code: defaultCode,
      message:
        'message' in errorObj && typeof errorObj.message === 'string'
          ? errorObj.message
          : defaultMessage,
    };
  }

  // Fallback for primitive or null errors
  return {
    code: defaultCode,
    message: defaultMessage,
  };
};

/**
 * Get device information for tracking
 */
const getDeviceInfo = (): { device_name: string; user_agent: string } => {
  if (typeof window === 'undefined') {
    return {
      device_name: 'Server',
      user_agent: 'Node.js',
    };
  }

  const userAgent = window.navigator?.userAgent;

  // React Native (and some embedded webviews) expose a `window` but no UA
  // string; reading `.includes` on an undefined `userAgent` below would throw
  // — which the login flow then mislabels as an auth error. Bail to a generic
  // device descriptor before the browser sniffing.
  if (!userAgent) {
    return {
      device_name: 'Mobile App',
      user_agent: 'ReactNative',
    };
  }

  let deviceName = 'Unknown Device';

  // Simple device detection
  if (userAgent.includes('Mobile') || userAgent.includes('Android')) {
    deviceName = 'Mobile Device';
  } else if (userAgent.includes('iPad') || userAgent.includes('iPhone')) {
    deviceName = 'iOS Device';
  } else if (userAgent.includes('Macintosh')) {
    deviceName = 'Mac';
  } else if (userAgent.includes('Windows')) {
    deviceName = 'Windows PC';
  } else {
    deviceName = 'Web Browser';
  }

  return {
    device_name: deviceName,
    user_agent: userAgent,
  };
};

// =============================================================================
// AUTHENTICATION SERVICE CLASS
// =============================================================================

export class AuthService {
  private options: Omit<Required<AuthServiceOptions>, 'tokenStorageService'>;
  private state: AuthServiceState;
  private customBearerLifecycle?: BearerLifecycleService;
  private tokenStorageService?: ITokenStorageService;

  /**
   * An app's pending two-factor sign-in: the challenge `/sanctum/token`
   * answered with, and when it lapses. In memory only, never stored. It's
   * dropped once spent, refused or lapsed, and by a new sign-in or a sign-out.
   */
  private pendingTwoFactor: { challenge: string; expiresAt: number } | null =
    null;

  constructor(
    options: AuthServiceOptions = {},
    customTokenRefreshService?: TokenRefreshService
  ) {
    this.options = { ...buildDefaultOptions(), ...options };
    // A custom refresh service gets a lifecycle of its own; otherwise every
    // caller shares the one lifecycle, and with it the one in-flight refresh.
    this.customBearerLifecycle = customTokenRefreshService
      ? new BearerLifecycleService(customTokenRefreshService)
      : undefined;

    // Store provided token storage, DI lookup will be done lazily
    this.tokenStorageService = options.tokenStorageService;

    this.state = {
      isInitialized: false,
      lastError: null,
    };

    // Initialize async - will be called on first use
    this.initialize().catch((error) => {
      console.warn('Auth service initialization failed:', error);
    });
  }

  // ===========================================================================
  // INITIALIZATION AND STATE MANAGEMENT
  // ===========================================================================

  /**
   * Initialize the auth service
   */
  private async initialize(): Promise<void> {
    try {
      if (this.options.mode === 'session') {
        // Stateful authentication initialization
        await this.initializeCSRF();
      }
    } catch (error) {
      console.warn('Auth service initialization failed:', error);
    } finally {
      this.setState({ isInitialized: true });
    }
  }

  /**
   * Get current authentication state
   */
  public getState(): Readonly<AuthServiceState> {
    return { ...this.state };
  }

  // ===========================================================================
  // CSRF AND SESSION MANAGEMENT
  // ===========================================================================

  /**
   * Initialize CSRF token for stateful authentication
   */
  private async initializeCSRF(): Promise<void> {
    if (this.options.mode !== 'session') return;

    try {
      await getAuthClient().get(SANCTUM_CONFIG.csrfCookiePath);
    } catch (error) {
      console.warn('Failed to initialize CSRF token:', error);
    }
  }

  /**
   * Manually refresh CSRF token if needed
   */
  public async refreshCSRF(): Promise<void> {
    if (this.options.mode !== 'session') {
      throw new Error('CSRF refresh is only available in session mode');
    }

    try {
      await getAuthClient().get(SANCTUM_CONFIG.csrfCookiePath);
    } catch (error) {
      throw createAuthError(
        error,
        AUTH_ERROR_CODES.CSRF_TOKEN_EXPIRED,
        'Failed to refresh CSRF token'
      );
    }
  }

  // ===========================================================================
  // AUTHENTICATION METHODS
  // ===========================================================================

  /**
   * Login user with email and password (supports both token and session modes)
   */
  public async login(
    credentials: LoginRequest
  ): Promise<LoginResponse | SessionLoginResponse | TwoFactorRequired> {
    this.setState({ lastError: null });
    // A new sign-in replaces one still waiting for its code.
    this.pendingTwoFactor = null;

    // Validate input data using zod schema
    const parseResult = safeValidateLogin(credentials);
    if (!parseResult.success) {
      throw createValidationError(parseResult.error, 'Login');
    }

    try {
      if (this.options.mode === 'session') {
        // Ensure CSRF token is initialized for session-based auth
        await this.initializeCSRF();
      }

      const deviceInfo = this.options.enableDeviceTracking
        ? getDeviceInfo()
        : {};
      const requestData = { ...credentials, ...deviceInfo };

      if (this.options.mode === 'token') {
        // Token-based authentication (mobile): hit the API token endpoint, not
        // the web `/login` route, which is CSRF-protected by the `web`
        // middleware group and would 419 for a native client. The user and
        // token arrive inside the `data` envelope, like the rest of the API.
        const response =
          await getAuthClient().post<TokenPasswordLoginApiResponse>(
            `${this.options.baseURL}${API_ENDPOINTS.TOKEN_LOGIN.path}`,
            requestData
          );

        const answer = response.data.data;

        // A two-factor account gets no bearer yet: keep the challenge for
        // `twoFactorChallenge`.
        if ('two_factor' in answer) {
          this.pendingTwoFactor = {
            challenge: answer.challenge,
            expiresAt: dayjs(answer.expires_at).valueOf(),
          };
          return { two_factor: true };
        }

        await this.storeToken(answer.token);

        return answer;
      } else {
        // Session-based authentication. A two-factor account is answered
        // `{ two_factor: true }` and no user: the sign-in waits in the
        // session for `twoFactorChallenge`.
        const response = await getAuthClient().post<
          SessionLoginResponse | TwoFactorRequired
        >(
          `${this.options.baseURL}${API_ENDPOINTS.SESSION_LOGIN.path}`,
          requestData
        );

        return response.data;
      }
    } catch (error) {
      const authError = createAuthError(
        error,
        AUTH_ERROR_CODES.INVALID_CREDENTIALS,
        'Invalid email or password'
      );

      this.setState({
        lastError: authError,
      });

      throw authError;
    }
  }

  /**
   * Validate invitation token
   */
  public async validateInvitationToken(
    token: string
  ): Promise<TokenValidationResult> {
    try {
      const response = await getAuthClient().get<{ data: { email: string } }>(
        `${this.options.baseURL}/api/validate-token?token=${encodeURIComponent(token)}`
      );

      return {
        isValid: true,
        email: response.data.data.email,
      };
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        return {
          isValid: false,
          error: error.response?.data.message,
        };
      }

      if (error instanceof Error) {
        return {
          isValid: false,
          error: error.message,
        };
      }

      return {
        isValid: false,
        error: 'Could not validate token',
      };
    }
  }

  /**
   * Make the account an invitation asked for.
   *
   * The website's answer is 201 with no body and a signed-in session. An app
   * sends `device_name`, which asks for a bearer instead: the answer is the
   * `{ user, token }` a password sign-in gets, and the bearer is stored as a
   * sign-in's is. `device_name` is what tells the two apart, so the website
   * never sends it: with one it would get a bearer and no session.
   *
   * Refusals: a 422 for the account's fields (a taken email, the password
   * rules, a used invitation), and `RATE_LIMITED` past 20 an hour, which
   * counts refused requests too. Never retried.
   */
  public async register(
    userData: RegisterInput
  ): Promise<RegisterResponse | SessionRegisterResponse> {
    this.setState({ lastError: null });

    // Validate input data using zod schema
    const parseResult = safeValidateRegister(userData);
    if (!parseResult.success) {
      throw createValidationError(parseResult.error, 'Registration');
    }

    try {
      if (this.options.mode === 'token') {
        const response = await getAuthClient().post<
          SuccessApiResponse<RegisterResponse>
        >(`${this.options.baseURL}${API_ENDPOINTS.TOKEN_REGISTER.path}`, {
          ...userData,
          ...getDeviceInfo(),
        });

        const answer = response.data.data;
        if (answer.token) await this.storeToken(answer.token);

        return answer;
      } else {
        // Ensure CSRF token is initialized for session-based auth
        await this.initializeCSRF();

        const response = await getAuthClient().post<SessionRegisterResponse>(
          `${this.options.baseURL}${API_ENDPOINTS.SESSION_REGISTER.path}`,
          userData
        );

        return response.data;
      }
    } catch (error) {
      const authError = createAuthError(
        error,
        AUTH_ERROR_CODES.EMAIL_ALREADY_EXISTS,
        'Registration failed'
      );

      this.setState({
        lastError: authError,
      });

      throw authError;
    }
  }

  public async waitlist(data: WaitlistInput): Promise<void> {
    this.setState({ lastError: null });

    // Validate input data using zod schema
    const parseResult = safeValidateWaitlist(data);
    if (!parseResult.success) {
      throw createValidationError(parseResult.error, 'Waitlist');
    }

    try {
      if (this.options.mode === 'session') {
        // Ensure CSRF token is initialized for session-based auth
        await this.initializeCSRF();
      }

      await getAuthClient().post<{ message: string }>(
        `${this.options.baseURL}/api/waitlist`,
        data
      );
    } catch (error: unknown) {
      const authError = createAuthError(
        error,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'Unable to join waitlist. Please check your data and try again.'
      );

      this.setState({
        lastError: authError,
      });

      throw authError;
    }
  }

  /**
   * Revoke, on the server, the bearer this device holds. Token mode only.
   *
   * Forgetting a token locally is not ending the session: left alone it stays
   * valid until it expires, up to thirty days, for anyone who copied it off
   * the device. Everything that ends a session calls this first, while the
   * bearer is still in storage to sign the request with — the auth client
   * signs nothing itself.
   *
   * Throws when the request fails, so a caller can tell; both callers carry on
   * regardless, because a revoke that does not land leaves the token to
   * expire on its own, which is what happened before this existed. Bounded,
   * so a dead network cannot hang a sign-out.
   */
  public async revokeCurrentToken(): Promise<void> {
    if (this.options.mode !== 'token') return;

    const accessToken = await this.getTokenStorageService().getAccessToken();
    if (!accessToken) return;

    await getAuthClient().delete(
      `${this.options.baseURL}${API_ENDPOINTS.TOKEN_LOGOUT.path}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: TOKEN_REVOKE_TIMEOUT_MS,
      }
    );
  }

  /**
   * Logout user
   */
  public async logout(callAPI: boolean = true): Promise<void> {
    this.pendingTwoFactor = null;

    try {
      // Session logout hits the web `/logout` route, which is CSRF-protected by
      // the `web` middleware group — a bearer cannot pass it, so token mode
      // revokes its own token instead.
      if (callAPI && this.options.mode === 'session') {
        const logoutData: LogoutRequest = {};

        if (this.options.enableDeviceTracking) {
          logoutData.device_name = getDeviceInfo().device_name;
        }

        await getAuthClient().post<LogoutResponse>(
          `${this.options.baseURL}${API_ENDPOINTS.SESSION_LOGOUT.path}`,
          logoutData
        );
      }

      if (callAPI && this.options.mode === 'token') {
        await this.revokeCurrentToken();
      }
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
      this.setState({
        lastError: createAuthError(
          error,
          AUTH_ERROR_CODES.UNKNOWN_ERROR,
          'Logout API call failed'
        ),
      });
    } finally {
      // Clear tokens and state
      if (this.options.mode === 'token') {
        await this.getTokenStorageService().clearTokens();
      }
    }
  }

  /**
   * Leave a member's account an admin signed in to from the admin panel.
   *
   * Signing out is a write, and the session is read-only, so `/logout` would
   * only be refused: the admin leaves instead, and signs out of the panel from
   * there. On success the browser is the admin again — every member route now
   * answers 403 — so the caller sends it to `adminPanelUrl()` rather than
   * clearing any state. Throws the raw error, as the refusal (403, not
   * impersonating) needs no handling of its own.
   */
  public async leaveImpersonation(): Promise<void> {
    await getAuthClient().post(
      `${this.options.baseURL}${API_ENDPOINTS.IMPERSONATE_LEAVE.path}`
    );
  }

  /** The admin panel, where an admin goes back to after leaving a member's account. */
  public adminPanelUrl(): string {
    return `${this.options.baseURL}/admin`;
  }

  /**
   * Refresh authentication token (only for token-based auth)
   */
  public async refreshToken(): Promise<AuthToken> {
    if (this.options.mode !== 'token') {
      throw createAuthError(
        null,
        AUTH_ERROR_CODES.TOKEN_INVALID,
        'Token refresh is not available in session mode'
      );
    }

    // The lifecycle owns the refresh: one request in flight however many
    // callers, the new bearer written to storage and the store together, and
    // the session ended only when the API refuses the bearer. A failure here
    // does NOT clear the tokens — a dropped connection leaves a bearer that is
    // still perfectly good.
    try {
      return await this.bearerLifecycle().refresh();
    } catch (error) {
      throw createAuthError(
        error,
        refreshFailureCode(error),
        'Token refresh failed'
      );
    }
  }

  // ===========================================================================
  // SOCIAL SIGN-IN
  // ===========================================================================

  /**
   * Sign in with Google from an app (token mode). The `id_token` must be issued
   * for the WEB client id: that is the audience the API checks.
   *
   * An account answers with its tokens, which are stored. No account yet
   * answers with a pending sign-up for the user to answer with
   * `completeSignUp` or `declineSignUp`. A refusal keeps the API's `code`:
   * `UNAUTHENTICATED` (the token was refused), `FORBIDDEN` (sign-ups are
   * closed), `RESOURCE_CONFLICT` (the email already has an account, which a
   * sign-in never links) and `VALIDATION_ERROR` (the provider hasn't verified
   * the email, or shared none: show the message).
   */
  public async signInWithGoogle(
    request: GoogleSignInRequest
  ): Promise<SocialSignInResult> {
    return this.socialSignIn(
      API_ENDPOINTS.GOOGLE_SIGN_IN.path,
      request,
      'Google sign-in failed'
    );
  }

  /**
   * Sign in with Apple from the iOS app (token mode). Send the raw nonce whose
   * SHA-256 went to Apple, and the name whenever Apple shared it: it does so on
   * the first authorization only. A nonce works for one request, so after any
   * failure start a fresh Apple authorization rather than resending.
   *
   * Answers and refusals as `signInWithGoogle`, plus `SERVER_ERROR` when Apple
   * couldn't be reached.
   */
  public async signInWithApple(
    request: AppleSignInRequest
  ): Promise<SocialSignInResult> {
    return this.socialSignIn(
      API_ENDPOINTS.APPLE_SIGN_IN.path,
      request,
      'Sign in with Apple failed'
    );
  }

  /**
   * The session's user, or null when there is no session: the website's, or
   * in an app, the bearer's.
   *
   * The store's `fetchUser` reads the user through here: at a page load that
   * may follow a social sign-in's redirect (signed in on the server, signed
   * out in the persisted store), after a change the server made, and in an
   * app at launch and on coming back, so a change made elsewhere shows. No
   * session is an answer, not an error, so a 401 comes back as null, with
   * nothing logged and no session-expired handling on the website.
   */
  public async getSessionUser(): Promise<User | null> {
    if (this.options.mode === 'token') return this.getBearerUser();

    try {
      const response = await getAuthClient().get<SuccessApiResponse<User>>(
        `${this.options.baseURL}${API_ENDPOINTS.SESSION_USER.path}`
      );

      return response.data.data;
    } catch (error) {
      const status = (error as { response?: { status?: number } } | null)
        ?.response?.status;
      if (status === HTTP_STATUS.UNAUTHORIZED) return null;

      throw createAuthError(
        error,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'Could not check for a session'
      );
    }
  }

  /**
   * An app's user, read with its bearer. It goes through `apiClient`, which
   * signs it, and whose 401 rule applies: a request a refresh overtook is
   * sent again, and a refused bearer ends the session.
   */
  private async getBearerUser(): Promise<User | null> {
    try {
      const response = await apiClient.get<SuccessApiResponse<User>>(
        `${this.options.baseURL}${API_ENDPOINTS.SESSION_USER.path}`
      );

      return response.data.data;
    } catch (error) {
      if (isApiError(error) && error.status === HTTP_STATUS.UNAUTHORIZED) {
        return null;
      }

      throw createAuthError(
        error,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'Could not check for a session'
      );
    }
  }

  /**
   * Finish a two-factor sign-in, after `login` answered `{ two_factor: true }`,
   * with a code from the authenticator app or a recovery code. Five tries a
   * minute (`RATE_LIMITED`). A recovery code works once and is replaced by a
   * new one; so is an authenticator code.
   *
   * On the website the pending sign-in waits in the session:
   * `POST /two-factor-challenge` answers 204, and the account is read from the
   * session. A 422 under the field sent is a refused code, and also the answer
   * once the pending sign-in has lapsed.
   *
   * In the apps the challenge `login` kept is traded for the bearer, stored as
   * `login` stores one. A 422 under `code` or `recovery_code` is a refused
   * code, and the challenge still holds; a 422 under `challenge` means it's
   * spent, lapsed or withdrawn, and it's dropped. Past `expires_at` it isn't
   * sent at all: that refusal is made here, in the API's shape.
   */
  public async twoFactorChallenge(
    proof: TwoFactorProof
  ): Promise<LoginResponse | SessionLoginResponse> {
    if (this.options.mode === 'token') {
      return this.answerTwoFactorChallenge(proof);
    }

    try {
      await getAuthClient().post(
        `${this.options.baseURL}${API_ENDPOINTS.TWO_FACTOR_CHALLENGE.path}`,
        proof
      );
    } catch (error) {
      throw createAuthError(
        error,
        AUTH_ERROR_CODES.INVALID_CREDENTIALS,
        'That code didn’t work'
      );
    }

    const user = await this.getSessionUser();
    if (!user) {
      throw createAuthError(
        null,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'Couldn’t sign you in. Try again.'
      );
    }

    return { user };
  }

  /** An app's half of `twoFactorChallenge`: the kept challenge, then the bearer. */
  private async answerTwoFactorChallenge(
    proof: TwoFactorProof
  ): Promise<LoginResponse> {
    const pending = this.pendingTwoFactor;
    if (!pending || dayjs().valueOf() >= pending.expiresAt) {
      this.pendingTwoFactor = null;
      throw lapsedTwoFactorSignIn();
    }

    let session: TokenLoginApiResponse['data'];
    try {
      const response = await getAuthClient().post<TokenLoginApiResponse>(
        `${this.options.baseURL}${API_ENDPOINTS.TOKEN_TWO_FACTOR_CHALLENGE.path}`,
        { challenge: pending.challenge, ...proof }
      );
      session = response.data.data;
    } catch (error) {
      const authError = createAuthError(
        error,
        AUTH_ERROR_CODES.INVALID_CREDENTIALS,
        'That code didn’t work'
      );
      if (authError.field === 'challenge') this.pendingTwoFactor = null;
      throw authError;
    }

    // Spent by the right code, even if storing the bearer then fails.
    this.pendingTwoFactor = null;
    await this.storeToken(session.token);

    return session;
  }

  /**
   * The website's pending sign-up, read from its session after a provider sent
   * the browser to `/login?sso_pending=<provider>`. Session mode only: an app's
   * arrives with the sign-in that found no account.
   *
   * A lapsed, answered or unknown pending sign-up answers `NOT_FOUND`.
   */
  public async getPendingSignUp(): Promise<PendingSignUp> {
    if (this.options.mode !== 'session') {
      throw createAuthError(
        null,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        "An app's pending sign-up arrives with its sign-in"
      );
    }

    try {
      const response = await getAuthClient().get<
        SuccessApiResponse<PendingSignUp>
      >(`${this.options.baseURL}${API_ENDPOINTS.GET_PENDING_SIGN_UP.path}`);

      return response.data.data;
    } catch (error) {
      throw createAuthError(
        error,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'Could not read the pending sign-up'
      );
    }
  }

  /**
   * Make the account a pending sign-up asked for.
   *
   * An app passes the pending sign-up's `token` and gets the TOKEN_LOGIN
   * payload, whose tokens are stored. The website answers with its session
   * and gets the session user, signed in. A refusal keeps the API's `code`:
   * `NOT_FOUND` (lapsed, answered or unknown: start the sign-in again),
   * `RESOURCE_CONFLICT` (an account took the email meanwhile), `FORBIDDEN`
   * (sign-ups are closed), and for an app's Apple sign-up `UNAUTHENTICATED`
   * (Apple won't exchange its code any more) or `SERVER_ERROR` (Apple couldn't
   * be reached).
   */
  public async completeSignUp(
    token?: string
  ): Promise<LoginResponse | SessionLoginResponse> {
    this.setState({ lastError: null });

    try {
      if (this.options.mode === 'token') {
        const deviceInfo = this.options.enableDeviceTracking
          ? getDeviceInfo()
          : {};
        const response = await getAuthClient().post<TokenLoginApiResponse>(
          `${this.options.baseURL}${API_ENDPOINTS.COMPLETE_SIGN_UP.path}`,
          { token, ...deviceInfo }
        );

        const session = response.data.data;
        await this.storeToken(session.token);

        return session;
      }

      await this.initializeCSRF();

      const response = await getAuthClient().post<SuccessApiResponse<User>>(
        `${this.options.baseURL}${API_ENDPOINTS.COMPLETE_SIGN_UP.path}`
      );

      return { user: response.data.data };
    } catch (error) {
      const authError = createAuthError(
        error,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'Could not create the account'
      );

      this.setState({
        lastError: authError,
      });

      throw authError;
    }
  }

  /**
   * Decline a pending sign-up: the user has an account to sign in to, and
   * connects the provider from its settings instead. An app passes the pending
   * sign-up's `token`; the website answers with its session.
   */
  public async declineSignUp(token?: string): Promise<void> {
    try {
      if (this.options.mode === 'token') {
        await getAuthClient().delete(
          `${this.options.baseURL}${API_ENDPOINTS.DECLINE_SIGN_UP.path}`,
          { data: { token } }
        );
        return;
      }

      await this.initializeCSRF();
      await getAuthClient().delete(
        `${this.options.baseURL}${API_ENDPOINTS.DECLINE_SIGN_UP.path}`
      );
    } catch (error) {
      throw createAuthError(
        error,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'Could not decline the sign-up'
      );
    }
  }

  /**
   * An app's social sign-in: the API answers 200 with the account's tokens, or
   * 202 with a pending sign-up when there is none yet.
   */
  private async socialSignIn(
    path: string,
    request: GoogleSignInRequest | AppleSignInRequest,
    failureMessage: string
  ): Promise<SocialSignInResult> {
    this.setState({ lastError: null });

    // The website signs in with a provider by redirect; only the apps hold a
    // provider token to exchange for a bearer.
    if (this.options.mode !== 'token') {
      throw createAuthError(
        null,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'The website signs in with a provider by redirect'
      );
    }

    try {
      const deviceInfo = this.options.enableDeviceTracking
        ? getDeviceInfo()
        : {};
      const response = await getAuthClient().post<
        TokenLoginApiResponse | PendingSignUpApiResponse
      >(`${this.options.baseURL}${path}`, { ...request, ...deviceInfo });

      if (response.status === 202) {
        const pendingSignUp = (response.data as PendingSignUpApiResponse).data
          .pending_sign_up;

        return { type: 'pending', pendingSignUp };
      }

      const session = (response.data as TokenLoginApiResponse).data;
      await this.storeToken(session.token);

      return { type: 'signed-in', response: session };
    } catch (error) {
      const authError = createAuthError(
        error,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        failureMessage
      );

      this.setState({
        lastError: authError,
      });

      throw authError;
    }
  }

  // ===========================================================================
  // USER PROFILE MANAGEMENT
  // ===========================================================================

  /**
   * Update the profile: name, gender, or the role (client ↔ trainer). Only
   * the fields sent change. Goes through `apiClient`, so the website signs it
   * with its session and an app with its bearer; a 422 comes back as the
   * client's `ValidationErrorClass`, its messages keyed by field.
   */
  public async updateProfile(profileData: UpdateProfileRequest): Promise<void> {
    // Validate input data using zod schema
    const parseResult = safeValidateUpdateProfile(profileData);
    if (!parseResult.success) {
      throw createValidationError(parseResult.error, 'Profile update');
    }

    await apiClient.put(API_ENDPOINTS.UPDATE_PROFILE.path, profileData);
  }

  // ===========================================================================
  // PASSWORD MANAGEMENT
  // ===========================================================================

  /**
   * Request a password-reset link (supports both token and session modes).
   *
   * The website asks Fortify's `/forgot-password`, which sits behind Sanctum's
   * CSRF check, so a visitor who lands there with no XSRF cookie yet fetches
   * one first. A bearer client can never pass that check (React Native sends
   * no cookies and can't mirror the token into a header), so the apps ask the
   * stateless `/api/forgot-password` instead; it answers as Fortify does.
   */
  public async resetPassword(email: string): Promise<PasswordResetResponse> {
    const requestData: PasswordResetRequest = { email };

    // Validate input data using zod schema
    const parseResult = safeValidatePasswordResetRequest(requestData);
    if (!parseResult.success) {
      throw createValidationError(parseResult.error, 'Password reset request');
    }

    try {
      if (this.options.mode === 'session') {
        await this.initializeCSRF();
      }

      const endpoint =
        this.options.mode === 'token'
          ? API_ENDPOINTS.TOKEN_PASSWORD_RESET_REQUEST
          : API_ENDPOINTS.PASSWORD_RESET_REQUEST;
      const response = await getAuthClient().post<PasswordResetResponse>(
        `${this.options.baseURL}${endpoint.path}`,
        requestData
      );

      return response.data;
    } catch (error) {
      throw createAuthError(
        error,
        AUTH_ERROR_CODES.PASSWORD_RESET_FAILED,
        'Failed to send password reset email'
      );
    }
  }

  /**
   * Confirm password reset with token (supports both token and session modes)
   */
  public async confirmPasswordReset(
    resetData: PasswordResetConfirmRequest
  ): Promise<
    PasswordResetConfirmResponse | SessionPasswordResetConfirmResponse
  > {
    // Validate input data using zod schema
    const parseResult = safeValidatePasswordResetConfirm(resetData);
    if (!parseResult.success) {
      throw createValidationError(
        parseResult.error,
        'Password reset confirmation'
      );
    }

    try {
      if (this.options.mode === 'token') {
        // Token-based password reset confirmation
        const response =
          await getAuthClient().post<PasswordResetConfirmResponse>(
            `${this.options.baseURL}${API_ENDPOINTS.PASSWORD_RESET_CONFIRM.path}`,
            resetData
          );

        const { token } = response.data;

        // If token is provided, auto-login the user
        if (token) {
          const expiresAt = token.expires_at
            ? dayjs(token.expires_at).valueOf()
            : dayjs().add(token.expires_in, 'seconds').valueOf();

          await this.getTokenStorageService().setTokens(
            token.access_token,
            token.refresh_token,
            expiresAt
          );
        }

        return response.data;
      } else {
        // Session-based password reset confirmation
        await this.initializeCSRF();

        const response =
          await getAuthClient().post<SessionPasswordResetConfirmResponse>(
            `${this.options.baseURL}${API_ENDPOINTS.PASSWORD_RESET_CONFIRM.path}`,
            resetData
          );

        return response.data;
      }
    } catch (error) {
      throw createAuthError(
        error,
        AUTH_ERROR_CODES.RESET_TOKEN_INVALID,
        'Invalid or expired password reset token'
      );
    }
  }

  /**
   * Change the signed-in user's password. Goes through `apiClient`, so the
   * website signs it with its session and an app with its bearer; a 422 comes
   * back as the client's `ValidationErrorClass`, its messages keyed by field.
   */
  public async changePassword(
    passwordData: ChangePasswordRequest
  ): Promise<void> {
    // Validate input data using zod schema
    const parseResult = safeValidateChangePassword(passwordData);
    if (!parseResult.success) {
      throw createValidationError(parseResult.error, 'Change password');
    }

    await apiClient.put(
      API_ENDPOINTS.UPDATE_PROFILE_PASSWORD.path,
      passwordData
    );
  }

  /**
   * Permanently delete the authenticated user's account.
   *
   * Irreversible and immediate — there is no undo and no recovery window
   * (unlike the soft, undo-toast deletes used for plans/workouts/exercises).
   * The proof of intent — the current password, or `confirmation: 'DELETE'`
   * for an account with none — is sent as the request body; Axios carries a
   * DELETE body in `config.data`. On success the local session is torn down
   * the same way as logout so no stale credentials survive on either platform
   * (session cookies for web, secure-store token for mobile).
   */
  public async deleteAccount(proof: DeleteAccountRequest): Promise<void> {
    // Validate input data using zod schema
    const parseResult = safeValidateDeleteAccount(proof);
    if (!parseResult.success) {
      throw createValidationError(parseResult.error, 'Delete account');
    }

    try {
      await getAuthClient().delete(
        `${this.options.baseURL}${API_ENDPOINTS.DELETE_ACCOUNT.path}`,
        { data: parseResult.data }
      );
    } catch (error) {
      throw createAuthError(
        error,
        AUTH_ERROR_CODES.INVALID_CREDENTIALS,
        'Failed to delete account'
      );
    }

    // The account no longer exists — clear any locally stored credentials.
    // Skip the network call (there is no session left to revoke) and reuse
    // logout's teardown so both auth modes are handled.
    await this.logout(false);
  }

  // ===========================================================================
  // EMAIL VERIFICATION
  // ===========================================================================

  /**
   * Verify the account's email with the four parts of an emailed link: on the
   * website's public `/verify-email` page with the session, in the apps with
   * the bearer. A signed-out reader signs in, then sends the same parts again.
   *
   * The signature covers the whole URL, scheme and host included, and the
   * link is signed for `/api/email/verify/…`, so the call goes there at the
   * API's host (Fortify's root route refuses it), with `expires` and
   * `signature` exactly as they came and nothing else in the query. It uses
   * the auth client, so a signed-out visitor's 401 raises no session-expired
   * handling.
   *
   * Resolves once verified, or when it already was. A refusal keeps the API's
   * code: `UNAUTHENTICATED` when signed out (checked before the signature),
   * `FORBIDDEN` for a link that was altered, has lapsed (60 minutes), was
   * called at another host, is another account's, or predates an email
   * change, and `RATE_LIMITED` past six a minute.
   */
  public async verifyEmail(link: EmailVerificationLink): Promise<void> {
    const parseResult = safeValidateEmailVerificationLink(link);
    if (!parseResult.success) {
      throw createValidationError(parseResult.error, 'Email verification');
    }

    const { id, hash, expires, signature } = parseResult.data;
    const path = API_ENDPOINTS.VERIFY_EMAIL.path.replaceParam({ id, hash });
    // The apps sign it with the bearer. The website's session goes with the
    // auth client's credentials, and a signed-out app sends neither, which
    // the API answers with a 401.
    const accessToken =
      this.options.mode === 'token'
        ? await this.getTokenStorageService().getAccessToken()
        : null;

    try {
      await getAuthClient().get(`${this.options.baseURL}${path}`, {
        params: { expires, signature },
        ...(accessToken
          ? { headers: { Authorization: `Bearer ${accessToken}` } }
          : {}),
      });
    } catch (error) {
      throw createAuthError(
        error,
        AUTH_ERROR_CODES.UNKNOWN_ERROR,
        'Could not verify the email'
      );
    }
  }

  // ===========================================================================
  // UTILITY AND HELPER METHODS
  // ===========================================================================

  private bearerLifecycle(): BearerLifecycleService {
    return this.customBearerLifecycle || getBearerLifecycle();
  }

  /**
   * Keep a bearer the API issued. Parse `expires_at` as a native ISO-8601
   * string so it tolerates the backend's fractional-second / offset format; a
   * rigid format string would yield NaN and a token that looks instantly
   * expired.
   */
  private async storeToken(token: AuthToken): Promise<void> {
    const expiresAt = token.expires_at
      ? dayjs(token.expires_at).valueOf()
      : dayjs().add(token.expires_in, 'seconds').valueOf();

    await this.getTokenStorageService().setTokens(
      token.access_token,
      token.refresh_token,
      expiresAt
    );
  }

  /**
   * Get token storage service (lazy initialization with DI fallback)
   */
  private getTokenStorageService(): ITokenStorageService {
    if (this.tokenStorageService) {
      return this.tokenStorageService;
    }

    try {
      this.tokenStorageService = getTokenStorage();
      return this.tokenStorageService;
    } catch (_error) {
      // For backward compatibility, provide a more graceful fallback
      // eslint-disable-next-line do-errors/no-clobber-typed-error -- DI-registration lookup failure, not a coordinator ApiErrorClass; there is no typed API error to preserve here
      throw new Error(
        'No token storage service available. Either provide tokenStorageService in options or call registerTokenStorage() to set up dependency injection.'
      );
    }
  }

  /**
   * Update internal state
   */
  private setState(updates: Partial<AuthServiceState>): void {
    this.state = { ...this.state, ...updates };
  }

  /**
   * Get authentication headers for API requests
   */
  public async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (this.options.mode === 'token') {
      const token = await this.getTokenStorageService().getAccessToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Check if service is initialized
   */
  public isInitialized(): boolean {
    return this.state.isInitialized;
  }

  /**
   * Get last authentication error
   */
  public getLastError(): AuthError | null {
    return this.state.lastError;
  }

  /**
   * Clear last error
   */
  public clearError(): void {
    this.setState({ lastError: null });
  }

  /**
   * Destroy service instance
   */
  public destroy(): void {
    this.pendingTwoFactor = null;
    this.setState({
      isInitialized: false,
      lastError: null,
    });
  }
}

// =============================================================================
// DEFAULT EXPORT AND SINGLETON INSTANCE
// =============================================================================

/**
 * Default authentication service instance.
 *
 * LAZY: wrapped in a transparent proxy so the `AuthService` (and its
 * constructor-time `initialize()`, which prefetches the CSRF cookie in session
 * mode) is built on FIRST USE, not at module import. This matters because the
 * shared barrel is imported before a platform calls `registerAuthConfig` — an
 * eager singleton would (a) read auth config before it exists and (b) on mobile,
 * fall back to session mode and fire `/sanctum/csrf-cookie` at startup. The
 * singleton is only ever used by web (which has registered its config before
 * first touch); mobile resolves its own token service via the store and never
 * touches this, so it now never constructs there.
 */
let authServiceInstance: AuthService | null = null;
export const authService = createLazyProxy<AuthService>(() => {
  if (!authServiceInstance) {
    authServiceInstance = new AuthService();
  }
  return authServiceInstance;
}, false);

/**
 * Create a new authentication service with custom options
 */
export const createAuthService = (options: AuthServiceOptions): AuthService => {
  return new AuthService(options);
};

// NOTE: the old createTokenAuthService / createSessionAuthService factories were
// removed — auth mode + transport is now a per-platform constant from the
// injected config (registerAuthConfig), so `new AuthService()` already resolves
// the correct mode/transport. A mode-specific factory would only let the
// instance's mode drift from the config-driven client transport.

/**
 * Convenience functions for common operations
 */
export const AuthUtils = {
  /**
   * Check if error is due to expired session
   */
  isSessionExpired: (error: AuthError): boolean => {
    const sessionExpiredCodes = [
      AUTH_ERROR_CODES.TOKEN_EXPIRED,
      AUTH_ERROR_CODES.REFRESH_TOKEN_EXPIRED,
      AUTH_ERROR_CODES.UNAUTHORIZED,
    ] as const;
    return sessionExpiredCodes.includes(
      error.code as (typeof sessionExpiredCodes)[number]
    );
  },

  /**
   * Check if error is a validation error
   */
  isValidationError: (error: AuthError): boolean => {
    return !!error.details && typeof error.details === 'object';
  },

  /**
   * Extract field errors from validation error
   */
  getFieldErrors: (error: AuthError): Record<string, string[]> => {
    return error.details || {};
  },

  /**
   * Get first error message for a field
   */
  getFieldError: (error: AuthError, fieldName: string): string | null => {
    const fieldErrors = AuthUtils.getFieldErrors(error);
    return fieldErrors[fieldName]?.[0] || null;
  },

  /**
   * Format user display name
   */
  formatDisplayName: (user: User): string => {
    return user.username;
  },

  /**
   * Check if user email is verified
   */
  isEmailVerified: (user: User): boolean => {
    return user.email_verified;
  },
};
